import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Select from "react-select";
import CommonFooter from "../../core/common/footer/commonFooter";
import {
  MinusCircle,
  PlusCircle,
} from "feather-icons-react/build/IconComponents";
import CommonDeleteModal from "../../core/common/modal/commonDeleteModal";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import Barcode from "react-barcode";
import { Circles } from "react-loader-spinner";
import { api } from "../../api/client";
import { useReactToPrint } from "react-to-print";
import { getCurrentUserPerms, hasWrite, isCurrentUserAdmin } from "../../utils/perm";

// ---------- S3 helpers (same convention as subcategory.jsx) ----------
const normalizeKey = (value) => {
  if (!value) return "";

  let v = String(value).trim();
  v = v.replace(/^\/+/, "");

  // legacy typo fix
  v = v.replace(/^catelog\//i, "catalog/");

  return v;
};

const toPublicUrl = (value) => {
  if (!value) return "";

  const s = String(value).trim();

  // already full URL or base64
  if (s.startsWith("data:") || /^https?:\/\//i.test(s)) return s;

  // if backend sends root-relative path like /uploads/x.jpg
  if (s.startsWith("/")) {
    const apiBase = (process.env.REACT_APP_API_BASE_URL || "").replace(
      /\/+$/,
      "",
    );
    return `${apiBase}${s}`;
  }

  const key = normalizeKey(s);

  const imageBase = (
    process.env.REACT_APP_API_IMAGE_BASE_URL ||
    "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
  ).replace(/\/+$/, "");

  return `${imageBase}/${key}`;
};

const resolveProductImage = (images) => {
  if (!images) return "";

  // array
  if (Array.isArray(images)) {
    for (const item of images) {
      const resolved = resolveProductImage(item);
      if (resolved) return resolved;
    }
    return "";
  }

  // object
  if (typeof images === "object") {
    const candidate =
      images.url ||
      images.key ||
      images.src ||
      images.image ||
      images.path ||
      images.location ||
      images.filename ||
      images.original ||
      images.secure_url;

    return resolveProductImage(candidate);
  }

  // string
  return toPublicUrl(images);
};
// ---------------------------------------------------------------------

const PrintBarcode = () => {
  const user = useSelector((state) => state?.auth?.user);
  const userPerms = getCurrentUserPerms(user?.permissions || []);
  const canWriteBarcode =
    isCurrentUserAdmin(user) ||
    userPerms.includes("*") ||
    hasWrite(userPerms, "barcode");

  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [productList, setProductList] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [storeOptions, setStoreOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const printRef = useRef(null);

  const visibleProducts = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return productList;

    const includes = (s) => (s || "").toString().toLowerCase().includes(q);

    return productList.filter(
      (p) =>
        includes(p?.productName?.en) ||
        includes(p?.sku) ||
        includes(p?.itemCode),
    );
  }, [productList, searchQuery]);

  const printableItems = useMemo(
    () =>
      visibleProducts.flatMap((product) => {
        const qty = Math.max(1, Number(quantities[product?._id] || 1));
        const productNameEn = product?.productName?.en || "Product";
        const productNameAr = product?.productName?.ar || "";
        const storeNameEn = product?.store?.storeName?.en || "Store";
        const price = product?.price || 0;
        const barcodeValue = (product?.itemCode || product?.sku || "")
          .toString()
          .trim();
        const image =
          resolveProductImage(
            product?.image || product?.images || product?.productImage,
          ) || "/assets/img/by-default.png";

        if (!barcodeValue) return [];

        return Array.from({ length: qty }).map((_, i) => ({
          key: `${product._id}-${i}`,
          storeNameEn,
          productNameEn,
          productNameAr,
          price,
          image,
          barcodeValue,
        }));
      }),
    [visibleProducts, quantities],
  );
  const handleDecrement = (productId) => {
    if (!canWriteBarcode) return;
    setQuantities((prev) => ({
      ...prev,
      [productId]: Math.max((prev[productId] || 1) - 1, 1),
    }));
  };

  const handleIncrement = (productId) => {
    if (!canWriteBarcode) return;
    setQuantities((prev) => ({
      ...prev,
      [productId]: (prev[productId] || 1) + 1,
    }));
  };

  const handleQuantityChange = (productId, value) => {
    if (!canWriteBarcode) return;
    const numeric = parseInt(value);
    if (!isNaN(numeric) && numeric > 0) {
      setQuantities((prev) => ({ ...prev, [productId]: numeric }));
    }
  };

  useEffect(() => {
    const fetchStoresAndWarehouses = async () => {
      try {
        const [storeRes, warehouseRes] = await Promise.all([
          api.get(`${process.env.REACT_APP_API_BASE_URL}api/store/stores`),
          api.get(`${process.env.REACT_APP_API_BASE_URL}api/warehouse/list`),
        ]);

        const stores = (storeRes.data || []).map((store) => ({
          value: store._id,
          label: store.storeName?.en || store.CustomerName || "Unnamed Store",
        }));

        const warehouses = (warehouseRes.data || []).map((warehouse) => ({
          value: warehouse._id,
          label:
            warehouse.warehouseName?.en ||
            warehouse.warehouse ||
            "Unnamed Warehouse",
        }));

        setStoreOptions(stores);
        setWarehouseOptions(warehouses);
      } catch (err) {
        console.error("Failed to fetch stores or warehouses", err);
      }
    };

    fetchStoresAndWarehouses();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await api.get(
          `${process.env.REACT_APP_API_BASE_URL}api/products/barcode-list`,
          {
            params: {
              storeId: selectedStore?.value,
              warehouseId: selectedWarehouse?.value,
              search: searchQuery,
            },
          },
        );
        setProductList(response.data || []);
      } catch (error) {
        console.error("Error fetching products for barcode:", error);
      } finally {
        setLoading(false);
      }
    };

    const isValidMongoId = (id) => /^[a-f\d]{24}$/i.test(id);

    if (
      (selectedStore?.value && !isValidMongoId(selectedStore.value)) ||
      (selectedWarehouse?.value && !isValidMongoId(selectedWarehouse.value))
    ) {
      console.warn("Invalid storeId or warehouseId – skipping fetch.");
      setProductList([]);
      return;
    }

    if (selectedStore?.value || selectedWarehouse?.value || searchQuery) {
      fetchProducts();
    } else {
      setProductList([]);
    }
  }, [selectedStore, selectedWarehouse, searchQuery]);

  const handleRemoveFromTable = (productId) => {
    if (!canWriteBarcode) return;
    setProductList((prev) =>
      prev.filter((product) => product._id !== productId),
    );
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: "Barcodes",
    preserveAfterPrint: false,
    pageStyle: `
    @page { size: auto; margin: 0mm; }

    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

      /* Hide everything */
      body * { visibility: hidden !important; }

      /* Show only print root */
      #print-root, #print-root * { visibility: visible !important; }

      /* Put print root at top-left */
      #print-root {
        position: fixed;
        left: 0;
        top: 0;
        width: 100%;
        padding: 10mm;
        background: #fff;
      }

      /* Grid of squares */
      .barcode-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 8mm;
      }

      /* Square barcode card */
      .barcode-card {
        width: 50mm;
        height: 50mm;
        border: 1px solid #ddd;
        border-radius: 6px;
        padding: 4mm;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        page-break-inside: avoid;
        break-inside: avoid;
      }

      .barcode-top {
        text-align: center;
        font-size: 10px;
        line-height: 1.2;
      }

      /* Hide print button */
      .no-print { display: none !important; }
    }
  `,
  });

  return (
    <>
      <div className="page-wrapper notes-page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4 className="fw-bold">{window.translate("Print Barcode")}</h4>
                <h6>{window.translate("Manage your barcodes")}</h6>
              </div>
            </div>
            <div className="d-flex align-items-center">
              <ul className="table-top-head">
                <RefreshIcon />
                <CollapesIcon />
              </ul>
            </div>
          </div>

          <div className="barcode-content-list">
            <form>
              <div className="row">
                <div className="col-lg-6 col-12">
                  <div className="row seacrh-barcode-item mb-1">
                    <div className="col-sm-6 mb-3 seacrh-barcode-item-one">
                      <label className="form-label">
                        {window.translate("Warehouse")}
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <Select
                        classNamePrefix="react-select"
                        options={warehouseOptions}
                        value={selectedWarehouse}
                        onChange={(option) => setSelectedWarehouse(option)}
                        placeholder="Choose"
                      />
                    </div>
                    <div className="col-sm-6 mb-3 seacrh-barcode-item-one">
                      <label className="form-label">
                        {window.translate("Store")}
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <Select
                        classNamePrefix="react-select"
                        options={storeOptions}
                        value={selectedStore}
                        onChange={(option) => setSelectedStore(option)}
                        placeholder="Choose"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-lg-6">
                  <div className="mb-3 search-form seacrh-barcode-item">
                    <div className="searchInput">
                      <label className="form-label">
                        {window.translate("Product")}
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control text-gray-3"
                        placeholder="Search Product by Code"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <div className="resultBox"></div>
                      <div className="icon">
                        <i className="fas fa-search" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>

            <div className="col-lg-12">
              <div className="modal-body-table search-modal-header bg-light p-2 p-sm-4">
                <div className="table-responsive border rounded-1 barcode-table">
                  {loading ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ minHeight: "200px" }}
                    >
                      <Circles
                        height={60}
                        width={60}
                        color="#0d6efd"
                        ariaLabel="loading"
                      />
                    </div>
                  ) : (
                    <table className="table datatable mb-0">
                      <thead>
                        <tr>
                          <th>{window.translate("Product")}</th>
                          <th>{window.translate("SKU")}</th>
                          <th>{window.translate("Code")}</th>
                          <th>{window.translate("Qty")}</th>
                          {canWriteBarcode && (
                            <th className="text-center no-sort bg-secondary-transparent" />
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {visibleProducts.map((product, index) => {
                          const imgSrc =
                            resolveProductImage(
                              product?.image ||
                                product?.images ||
                                product?.productImage,
                            ) || "/assets/img/by-default.png";

                          return (
                            <tr key={index}>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <Link
                                    to="#"
                                    className="avatar avatar-md me-2"
                                  >
                                    <img
                                      src={imgSrc}
                                      alt={
                                        product?.productName?.en || "Product"
                                      }
                                      className="img-fluid"
                                      style={{
                                        width: 40,
                                        height: 40,
                                        objectFit: "cover",
                                        borderRadius: 6,
                                      }}
                                    />
                                  </Link>
                                  <Link to="#">{product?.productName?.en}</Link>
                                </div>
                              </td>
                              <td>{product?.sku}</td>
                              <td>{product?.itemCode}</td>
                              <td>
                                <div className="product-quantity border-secondary-transparent">
                                  <span
                                    className="quantity-btn"
                                    onClick={() => handleDecrement(product._id)}
                                  >
                                    <MinusCircle className="feather-search" />
                                  </span>
                                  <input
                                    type="text"
                                    className="quntity-input"
                                    value={quantities[product._id] || 1}
                                    disabled={!canWriteBarcode}
                                    onChange={(e) =>
                                      handleQuantityChange(
                                        product._id,
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <span
                                    className="quantity-btn"
                                    onClick={() => handleIncrement(product._id)}
                                  >
                                    <PlusCircle className="plus-circle" />
                                  </span>
                                </div>
                              </td>
                              {canWriteBarcode && (
                                <td className="action-table-data">
                                  <div className="edit-delete-action">
                                    <Link
                                      className="barcode-delete-icon"
                                      to="#"
                                      onClick={() =>
                                        handleRemoveFromTable(product._id)
                                      }
                                    >
                                      <i
                                        data-feather="x-circle"
                                        className="feather-x-circle text-danger"
                                      />
                                    </Link>
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            <div className="paper-search-size">
              <div className="row align-items-center">
                <div className="col-lg-6">
                  <form className="mb-0">
                    {/* Paper size controls (optional) */}
                  </form>
                </div>
                <div className="col-lg-6 pt-3">{/* toggles (optional) */}</div>
              </div>
            </div>

            {canWriteBarcode && (
              <div className="search-barcode-button">
                <Link
                  to="#"
                  className="btn btn-submit btn-primary me-2 mt-0"
                  data-bs-toggle="modal"
                  data-bs-target="#prints-barcode"
                >
                  <span>
                    <i className="fas fa-eye ms-2 me-2" />
                  </span>
                  {window.translate("Generate Barcode")}
                </Link>
              </div>
            )}
          </div>
        </div>
        <CommonFooter />
      </div>

      {/* Print Barcode */}
      <div className="modal fade" id="prints-barcode">
        <div className="modal-dialog modal-dialog-centered stock-adjust-modal barcode-modal">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header">
                  <div className="page-title">
                    <h4>{window.translate("barcode")}</h4>
                  </div>
                  <button
                    type="button"
                    className="close bg-danger text-white fs-16 shadow-none"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>

                <div className="modal-body">
                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-danger shadow-none no-print"
                      onClick={handlePrint}
                      disabled={!canWriteBarcode || printableItems.length === 0}
                    >
                      <i className="fas fa-print me-2" />
                      {window.translate("printBarcode")}
                    </button>
                  </div>

                  {/* ✅ PRINT AREA (must be mounted all the time) */}
                  <div
                    id="print-root"
                    ref={printRef}
                    style={{
                      background: "#fff",
                      paddingTop: 12,
                      maxHeight: "55vh",
                      overflowY: "auto",
                    }}
                  >
                    {printableItems.length === 0 ? (
                      <div className="text-muted py-3">
                        {window.translate("There is nothing to print")}
                      </div>
                    ) : (
                      <div className="barcode-grid">
                        {printableItems.map((item) => (
                          <div className="barcode-card" key={item.key}>
                            <div
                              className="barcode-top"
                              style={{ width: "100%" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  marginBottom: 4,
                                }}
                              >
                                <img
                                  src={item.image}
                                  alt={item.productNameEn}
                                  style={{
                                    width: 34,
                                    height: 34,
                                    objectFit: "cover",
                                    borderRadius: 4,
                                    border: "1px solid #ddd",
                                  }}
                                />
                              </div>

                              <div style={{ fontWeight: 700 }}>
                                {item.storeNameEn}
                              </div>

                              <div style={{ opacity: 0.9, fontWeight: 600 }}>
                                {item.productNameEn}
                              </div>

                              <div
                                style={{
                                  opacity: 0.85,
                                  direction: "rtl",
                                  textAlign: "center",
                                  fontSize: 9,
                                }}
                              >
                                {item.productNameAr}
                              </div>

                              <div>
                                {window.translate("Price")}: {item.price}
                              </div>
                            </div>

                            <Barcode
                              value={item.barcodeValue}
                              height={40}
                              width={1.4}
                              fontSize={10}
                              displayValue={true}
                              margin={0}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Print Barcode */}

      <CommonDeleteModal />
    </>
  );
};

export default PrintBarcode;
